import React from "react";
import { createRoot } from "react-dom/client";
import { bootstrapConfig, configBanner } from "./lib/config";
import { App } from "./App";
import { ToolFairPayFilm } from "./tools/tool-fair-pay-film";
import { ToolUrenFilm } from "./tools/tool-uren-film";
import { ToolFairPayFestival } from "./tools/tool-fair-pay-festival";

// Eenvoudige path-routing: geen react-router dependency.
// /fairpay-calculatietool      → volledige calculator
// /timesheets                  → losse tool (film/AV, alleen timesheet)
// /starttarieven-filmav        → losse tool (film/AV, rol + tarief)
// /starttarieven-filmfestival  → losse tool (festival, vorm + rol + tarief)
// Het algemene tools-overzicht leeft op een aparte omgeving (niet in deze repo).
const CALCULATOR_PATH = "/fairpay-calculatietool";

// Per-route SEO-meta. Client-side gezet (SPA met één index.html): werkt voor
// de browser-tab en JS-renderende crawlers (Google). Link-previews zonder JS
// vallen terug op de statische meta in index.html.
const META = {
  [CALCULATOR_PATH]: {
    title: "Fair Pay calculatietool voor de filmsector | Filmwerkwijs",
    description: "Bereken een onderbouwd openingsbod of salarisindicatie voor de Nederlandse film-, AV- en festivalsector. Voor zzp'ers én loondienst, stap voor stap.",
  },
  "/timesheets": {
    title: "Uren & timesheet berekenen film/AV | Filmwerkwijs",
    description: "Schat per departement de uren voor je filmopdracht met kant-en-klare timesheet-sjablonen, een onderbouwde basis voor je tarief in de film- en AV-sector.",
  },
  "/starttarieven-filmav": {
    title: "Fair Pay starttarieven film & AV | Filmwerkwijs",
    description: "Check het Fair Pay starttarief voor jouw rol in de Nederlandse film- en AV-sector, op basis van je functie en zwaarteniveau.",
  },
  "/starttarieven-filmfestival": {
    title: "Fair Pay starttarieven filmfestivals | Filmwerkwijs",
    description: "Check het Fair Pay starttarief of salaris voor jouw functie bij een Nederlands filmfestival. Voor zzp'ers en loondienst, op basis van de cao-schalen.",
  },
};

const setMeta = (path) => {
  const m = META[path] || META[CALCULATOR_PATH];
  document.title = m.title;
  const set = (sel, val) => document.querySelector(sel)?.setAttribute("content", val);
  set('meta[name="description"]', m.description);
  set('meta[property="og:title"]', m.title);
  set('meta[property="og:description"]', m.description);
};

const pickRoot = () => {
  const path = location.pathname.replace(/\/+$/, "") || "/";
  if (META[path]) setMeta(path);
  switch (path) {
    case CALCULATOR_PATH:                 return <App />;
    case "/timesheets":                  return <ToolUrenFilm />;
    case "/starttarieven-filmav":        return <ToolFairPayFilm />;
    case "/starttarieven-filmfestival":  return <ToolFairPayFestival />;
    default:
      // `/` en onbekende paden redirecten naar de volledige calculator.
      // De hash (#berekening=…) blijft behouden zodat deellinks blijven werken.
      location.replace(CALCULATOR_PATH + location.search + location.hash);
      return null;
  }
};

const ConfigBanner = () => {
  const b = configBanner.value;
  if (!b) return null;
  const palette = {
    error:   { bg: "#FDECEA", fg: "#8B1F1A", border: "#F4B7B1" },
    warning: { bg: "#FFF4D6", fg: "#6B4B00", border: "#F0D071" },
    info:    { bg: "#E7F1FE", fg: "#1A3F72", border: "#B5CEF2" },
  }[b.type] || { bg: "#FFF4D6", fg: "#6B4B00", border: "#F0D071" };
  return (
    <div style={{
      background: palette.bg, color: palette.fg, border: `1px solid ${palette.border}`,
      borderRadius: 8, padding: "10px 14px", margin: "12px 40px 0",
      fontSize: 14, fontFamily: "var(--font-sans)", maxWidth: 1240,
      marginLeft: "auto", marginRight: "auto",
    }}>
      <i className="ri-information-line" style={{ marginRight: 8 }} />
      {b.text}
    </div>
  );
};

// Externe links openen standaard in een nieuw tabblad. Delegated listener
// vangt elke <a href="http(s)://…"> ongeacht waar in de tree gerenderd.
// Interne (#hash) en native handlers (mailto:/tel:) blijven onaangetast.
document.addEventListener("click", (e) => {
  const a = e.target.closest?.("a[href]");
  if (!a) return;
  const href = a.getAttribute("href");
  if (!href || !/^https?:\/\//i.test(href)) return;
  if (a.target === "_blank") return;
  e.preventDefault();
  window.open(href, "_blank", "noopener,noreferrer");
});

(async () => {
  try {
    await bootstrapConfig();
  } catch (err) {
    console.error("[bootstrap] Config kon niet geladen worden", err);
    configBanner.value = {
      type: "error",
      text: "Configuratie kon niet geladen worden. Herlaad de pagina of neem contact op.",
    };
  }
  createRoot(document.getElementById("config-banner")).render(<ConfigBanner />);
  createRoot(document.getElementById("root")).render(pickRoot());
})();
